import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { Question } from '../types';
import {
  type SavedQuestion as StarredQuestion,
  subscribeSavedQuestions,
  getSavedQuestionsSnapshot,
  saveQuestion,
  deleteSavedQuestion,
  listSavedQuestions,
} from '../api/savedQuestionsApi';

export type { StarredQuestion };

export function useStarredQuestions(userId: number | null) {
  const effectiveUserId = userId;

  const starredQuestions = useSyncExternalStore(
    subscribeSavedQuestions,
    () => getSavedQuestionsSnapshot(effectiveUserId),
    () => getSavedQuestionsSnapshot(effectiveUserId),
  );

  useEffect(() => {
    if (!effectiveUserId) return;
    void listSavedQuestions(effectiveUserId).catch((err) => {
      console.error('Failed to load saved questions:', err);
    });
  }, [effectiveUserId]);

  const addQuestion = useCallback(
    (question: Question, userAnswer?: string) => {
      void saveQuestion(effectiveUserId, question, userAnswer).catch((err) => {
        console.error('Failed to save question:', err);
      });
    },
    [effectiveUserId],
  );

  const removeQuestion = useCallback(
    (starredId: string) => {
      void deleteSavedQuestion(effectiveUserId, starredId).catch((err) => {
        console.error('Failed to remove saved question:', err);
      });
    },
    [effectiveUserId],
  );

  const isQuestionStarred = useCallback(
    (questionId: string) => {
      if (!effectiveUserId) return false;
      return getSavedQuestionsSnapshot(effectiveUserId).some((item) => item.questionId === questionId);
    },
    [effectiveUserId],
  );

  const getLatest = useCallback(() => listSavedQuestions(effectiveUserId), [effectiveUserId]);

  return {
    starredQuestions,
    addQuestion,
    removeQuestion,
    isQuestionStarred,
    getLatest,
  };
}

